/**
 * API wrapper with centralized error handling
 * Handles 403 (Forbidden) and 404 (Not Found) errors with user notifications
 */

import i18n from '../i18n/config';

export interface ApiResponse<T = any> {
    data: T | null;
    error: ApiError | null;
    status: number;
}

export interface ApiError {
    status: number;
    message: string;
}

/**
 * Get translated error message based on status code
 */
function getErrorMessage(status: number): string {
    const t = i18n.t.bind(i18n);

    switch (status) {
        case 401:
            return t('Your session has expired. Please log in again.');
        case 403:
            return t('You do not have permission to perform this action.');
        case 404:
            return t('The requested resource was not found.');
        case 500:
            return t('Server error. Please try again later.');
        default:
            return t('An error occurred. Please try again.');
    }
}

/**
 * Show error notification to user
 */
export function showErrorNotification(message: string): void {
    // Dispatch custom event for notification system
    const event = new CustomEvent('api-error', {
        detail: { message, type: 'error' }
    });
    window.dispatchEvent(event);
}

/**
 * Show success notification to user
 */
export function showSuccessNotification(message: string): void {
    const event = new CustomEvent('api-success', {
        detail: { message, type: 'success' }
    });
    window.dispatchEvent(event);
}

/**
 * Options for handleApiError
 */
export interface HandleApiErrorOptions {
    /** Whether to show notification to user (default: true) */
    showNotification?: boolean;
}

/**
 * Handle API response errors
 * Returns true if error was handled (caller should stop processing)
 */
export function handleApiError(
    response: Response,
    navigate?: (path: string) => void,
    options: HandleApiErrorOptions = {}
): boolean {
    if (response.ok) {
        return false;
    }

    const { showNotification = true } = options;
    const message = getErrorMessage(response.status);

    switch (response.status) {
        case 401:
            // Session expired - show error notification but don't force logout
            // This prevents users from losing unsaved work (e.g. while editing a task)
            if (showNotification) {
                showErrorNotification(message);
            }
            return true;

        case 403:
            // Forbidden - user doesn't have permission
            if (showNotification) {
                showErrorNotification(message);
            }
            return true;

        case 404:
            // Not found
            if (showNotification) {
                showErrorNotification(message);
            }
            return true;

        default:
            // Other errors
            if (showNotification) {
                showErrorNotification(message);
            }
            return true;
    }
}

/**
 * Safely parse JSON from response, returning null for empty bodies
 * Use this instead of response.json() to handle empty responses
 */
export async function safeJsonParse(response: Response): Promise<any> {
    // Handle 204 No Content
    if (response.status === 204) {
        return null;
    }

    // Check Content-Length header if available
    const contentLength = response.headers.get('Content-Length');
    if (contentLength === '0') {
        return null;
    }

    // Try to read the body as text first
    const text = await response.text();

    // If empty, return null
    if (!text || text.trim() === '') {
        return null;
    }

    // Try to parse as JSON
    try {
        return JSON.parse(text);
    } catch (e) {
        console.warn('Failed to parse response as JSON:', text.substring(0, 100));
        return null;
    }
}

/**
 * Get default request headers with authentication
 */
export function getAuthHeaders(): HeadersInit {
    return {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + localStorage.getItem('token')
    };
}

/**
 * Make an authenticated API request with error handling
 */
export async function apiRequest<T = any>(
    url: string,
    options: RequestInit = {},
    navigate?: (path: string) => void
): Promise<ApiResponse<T>> {
    const fullUrl = url.startsWith('http') ? url : import.meta.env.VITE_API_URL + url;

    const requestOptions: RequestInit = {
        ...options,
        headers: {
            ...getAuthHeaders(),
            ...options.headers
        }
    };

    try {
        const response = await fetch(fullUrl, requestOptions);

        if (!response.ok) {
            handleApiError(response, navigate);
            return {
                data: null,
                error: {
                    status: response.status,
                    message: getErrorMessage(response.status)
                },
                status: response.status
            };
        }

        // Handle empty responses (204 No Content)
        if (response.status === 204) {
            return {
                data: null,
                error: null,
                status: response.status
            };
        }

        const data = await response.json();
        return {
            data,
            error: null,
            status: response.status
        };
    } catch (error) {
        const message = i18n.t('Network error. Please check your connection.');
        showErrorNotification(message);
        return {
            data: null,
            error: {
                status: 0,
                message
            },
            status: 0
        };
    }
}

/**
 * API GET request
 */
export function apiGet<T = any>(url: string, navigate?: (path: string) => void): Promise<ApiResponse<T>> {
    return apiRequest<T>(url, { method: 'GET' }, navigate);
}

/**
 * API POST request
 */
export function apiPost<T = any>(url: string, body: any, navigate?: (path: string) => void): Promise<ApiResponse<T>> {
    return apiRequest<T>(url, {
        method: 'POST',
        body: JSON.stringify(body)
    }, navigate);
}

/**
 * API PUT request
 */
export function apiPut<T = any>(url: string, body: any, navigate?: (path: string) => void): Promise<ApiResponse<T>> {
    return apiRequest<T>(url, {
        method: 'PUT',
        body: JSON.stringify(body)
    }, navigate);
}

/**
 * API DELETE request
 */
export function apiDelete<T = any>(url: string, navigate?: (path: string) => void): Promise<ApiResponse<T>> {
    return apiRequest<T>(url, { method: 'DELETE' }, navigate);
}
